import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, MessageCircle, Send, Sparkles, Trash2, X } from "lucide-react";
import { API } from "../lib/api";
import { useSiteContent } from "../lib/site";
import { useAuth } from "../context/AuthContext";

const SUGGESTIONS = [
    "ATA Angora nedir, nasıl çalışır?",
    "Nasıl tedarikçi olabilirim?",
    "Nasıl mağaza kaydı oluşturabilirim?",
    "Üyelik süreci nasıl işliyor?",
];

const WELCOME = "Merhaba! Ben ATA AI — ATA Angora Tedarik Platformu'nun tanıtım asistanıyım. Platform, üyelik ve çalışma modeli hakkında merak ettiklerinizi sorabilirsiniz.";

export default function AtaAiWidget() {
    const { user, loading } = useAuth();
    const location = useLocation();
    const siteContent = useSiteContent();
    let waNumber = (siteContent.site_whatsapp || "").replace(/\D/g, "");
    if (waNumber.startsWith("0")) waNumber = "9" + waNumber.slice(1);
    else if (waNumber.length === 10) waNumber = "90" + waNumber;
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const scrollRef = useRef(null);
    const loadedRef = useRef(false);

    const [sessionId] = useState(() => {
        let s = sessionStorage.getItem("ata_ai_session");
        if (!s) {
            s = crypto.randomUUID();
            sessionStorage.setItem("ata_ai_session", s);
        }
        return s;
    });

    useEffect(() => {
        if (open && !loadedRef.current) {
            loadedRef.current = true;
            fetch(`${API}/ai/public/history?session_id=${sessionId}`)
                .then((r) => (r.ok ? r.json() : []))
                .then((data) => setMessages(Array.isArray(data) ? data : []))
                .catch(() => {});
        }
    }, [open, sessionId]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, open]);

    if (loading || user || location.pathname.startsWith("/panel")) return null;

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const resp = await fetch(`${API}/ai/public/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: msg, session_id: sessionId }),
            });
            if (!resp.ok || !resp.body) {
                let detail = "ATA AI şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.";
                try { detail = (await resp.json()).detail || detail; } catch { /* yoksay */ }
                setMessages((m) => {
                    const copy = [...m];
                    copy[copy.length - 1] = { role: "assistant", content: detail };
                    return copy;
                });
                return;
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    const d = part.slice(6);
                    if (d === "[DONE]") break;
                    try {
                        const j = JSON.parse(d);
                        if (j.token) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.token };
                                return copy;
                            });
                        }
                        if (j.replace) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: j.replace };
                                return copy;
                            });
                        }
                        if (j.error) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: "Bir hata oluştu. Lütfen tekrar deneyin." };
                                return copy;
                            });
                        }
                    } catch { /* yoksay */ }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Bağlantı hatası. Lütfen tekrar deneyin." };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    const clear = async () => {
        await fetch(`${API}/ai/public/history?session_id=${sessionId}`, { method: "DELETE" }).catch(() => {});
        setMessages([]);
    };

    return (
        <>
            {open && (
                <div
                    className="fixed inset-x-4 bottom-40 z-40 flex h-[540px] max-h-[65vh] flex-col overflow-hidden rounded-2xl border border-amber-900/30 bg-stone-950 text-stone-100 shadow-2xl sm:inset-x-auto sm:right-4 sm:w-96"
                    data-testid="ata-ai-panel"
                >
                    <div className="flex items-center gap-3 border-b border-stone-800 p-4">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-terra-600 to-saffron-400">
                            <Sparkles className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                            <p className="font-display font-bold">ATA AI</p>
                            <p className="text-xs text-stone-400">Platform tanıtım asistanınız</p>
                        </div>
                        <button onClick={clear} className="rounded-md p-1.5 text-stone-400 transition-colors hover:text-white" data-testid="ata-ai-clear" title="Sohbeti temizle">
                            <Trash2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => setOpen(false)} className="rounded-md p-1.5 text-stone-400 transition-colors hover:text-white" data-testid="ata-ai-close" title="Kapat">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 scrollbar-thin" data-testid="ata-ai-messages">
                        <div className="flex justify-start">
                            <div className="max-w-[85%] rounded-2xl border border-stone-800 bg-stone-900 px-4 py-2.5 text-sm leading-relaxed text-stone-200" data-testid="ata-ai-welcome">
                                {WELCOME}
                            </div>
                        </div>
                        {messages.length === 0 && (
                            <div className="grid gap-2 pt-1">
                                {SUGGESTIONS.map((s, i) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="rounded-lg border border-stone-800 bg-stone-900 px-3 py-2 text-left text-xs text-stone-300 transition-colors hover:border-terra-500/50 hover:text-white"
                                        data-testid={`ata-ai-suggestion-${i}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                        m.role === "user" ? "bg-terra-600 text-white" : "border border-stone-800 bg-stone-900 text-stone-200"
                                    }`}
                                    data-testid={`ata-ai-message-${i}`}
                                >
                                    {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block h-4 w-2 animate-pulse bg-terra-500" /> : "")}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-stone-800 p-3">
                        {waNumber && (
                            <a
                                href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Merhaba, ATA Angora hakkında bilgi almak istiyorum.")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mb-2 flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                                data-testid="ata-ai-whatsapp"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp'tan Devam Et
                            </a>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Platform hakkında sorun..."
                                maxLength={600}
                                disabled={streaming}
                                className="h-10 flex-1 rounded-md border border-stone-800 bg-stone-900 px-3 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:ring-1 focus:ring-terra-500 disabled:opacity-50"
                                data-testid="ata-ai-input"
                            />
                            <button
                                type="submit"
                                disabled={streaming || !input.trim()}
                                className="flex h-10 w-10 items-center justify-center rounded-md bg-terra-600 text-white transition-colors hover:bg-terra-700 disabled:opacity-50"
                                data-testid="ata-ai-send"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <button
                onClick={() => setOpen((o) => !o)}
                className="fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-terra-600 to-saffron-400 text-white shadow-lg shadow-terra-900/40 transition-transform hover:scale-105 active:scale-95"
                data-testid="ata-ai-button"
                title="ATA AI — Tanıtım Asistanı"
                aria-label="ATA AI sohbet asistanını aç"
            >
                {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
            </button>
        </>
    );
}
